// Single dispatcher for the four suggestion buckets on /meet/:id/transcript.
// Each bucket maps via SUGGESTION_BUCKETS to an existing creation function on
// the project (saveEventToSupabase, saveTicketToSupabase, createNote,
// messageUser). applySuggestion just forwards.
//
// On success, writes a fingerprint entry to meet_transcript_analyses.applied_items
// (gated by the meet_analysis_update_applied RLS policy from 0018_meet.sql)
// and routes the user to the destination feature.
import { getSDK } from '../../../shared/functions/sdk.js'
import { SUGGESTION_BUCKETS, fingerprintSuggestion, pruneStaleApplied } from './meetUtils.js'

// Per-bucket payload adapters — the LLM extraction shape
// (calendar_events, tickets, notes, chat_messages) doesn't match the
// existing creation functions' shapes, so each bucket needs a small mapping.
// Each target function uses camelCase at its boundary and converts to
// snake_case internally; we do the same.

// Loose-parse a human proposed_time string into a YYYY-MM-DD + HH:MM pair.
// Gemini may produce "next Thursday at 2pm" or "2026-04-17 14:00" or similar.
// Try Date.parse first; fall back to tomorrow 10:00 if unparseable.
const _parseProposedTime = (str) => {
  if (!str) return null
  const d = new Date(str)
  if (!isNaN(d)) {
    const pad = n => String(n).padStart(2, '0')
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
  return null
}

// Adapters translate the Gemini extraction shape into the payload shape the
// target creation function expects. When `item.action === 'update'`, include
// `id` so saveEventToSupabase / saveTicketToSupabase hit their existing-row
// update path (those functions upsert when id is present — see tickets
// saveTicketToSupabase.js `_toRow`).
const _targetIdAsInt = (str) => {
  if (!str) return null
  const n = Number(str)
  return Number.isFinite(n) ? n : null
}

const ADAPTERS = {
  calendar_events: (item) => {
    const parsed = _parseProposedTime(item.proposed_time) || (() => {
      const t = new Date()
      t.setDate(t.getDate() + 1)
      const pad = n => String(n).padStart(2, '0')
      return {
        date: `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`,
        time: '10:00'
      }
    })()
    const [h, m] = parsed.time.split(':').map(Number)
    const endH = String((h + 1) % 24).padStart(2, '0')
    const payload = {
      title: item.title || 'Untitled event',
      date: parsed.date,
      startTime: parsed.time,
      endTime: `${endH}:${String(m).padStart(2, '0')}`,
      description: item.description || '',
      invitees: (item.attendees || []).filter(Boolean),
      type: 'meeting',
      meetingMode: 'symbols-meet',
      color: 'blue'
    }
    if (item.action === 'update') {
      const id = _targetIdAsInt(item.target_id)
      if (id != null) payload.id = id
    }
    return payload
  },
  tickets: (item) => {
    const payload = {
      title: item.title || 'Untitled ticket',
      body: item.description || '',
      assigneeEmail: item.assignee_email || null,
      columnKey: 'backlog'
    }
    if (item.action === 'update') {
      const id = _targetIdAsInt(item.target_id)
      if (id != null) payload.id = id
    }
    return payload
  },
  notes: (item) => {
    const payload = {
      title: item.title || 'Untitled note',
      content: item.content || '',
      tags: item.tags || [],
      source: 'ai'
    }
    if (item.action === 'update') {
      const id = _targetIdAsInt(item.target_id)
      if (id != null) payload.id = id
    }
    return payload
  },
  knowledge_base: (item) => {
    const payload = {
      title: item.title || 'Untitled article',
      category: item.category || 'general',
      summary: item.summary || '',
      content: item.content || item.summary || '',
      source: 'ai'
    }
    if (item.action === 'update') {
      const id = _targetIdAsInt(item.target_id)
      if (id != null) payload.id = id
    }
    return payload
  },
  chat_messages: (item) => ({
    email: item.to_email || '',
    body: item.body || ''
  })
}

export const applySuggestion = async function (bucket, item) {
  const cfg = SUGGESTION_BUCKETS[bucket]
  if (!cfg) {
    console.error('[applySuggestion] unknown bucket', bucket)
    return null
  }
  const root = this.state.root
  const adapter = ADAPTERS[bucket]
  const payload = adapter(item)

  let targetId = null
  try {
    if (bucket === 'chat_messages') {
      // chat case is special: messageUser opens (or creates) the DM with the
      // recipient and sets a draft body. The user manually sends — we never
      // auto-send chat messages.
      targetId = await this.call('messageUser', payload.email)
      // Stash the draft body on root so the chat composer picks it up after
      // navigation. Chat reads this slot in its Composer if present.
      root.update({ meetSuggestedDraft: payload.body })
    } else {
      targetId = await this.call(cfg.applyFn, payload)
    }
  } catch (err) {
    console.error('[applySuggestion] applyFn failed', cfg.applyFn, err)
    return null
  }
  if (!targetId) return null

  // Persist the application in meet_transcript_analyses.applied_items so
  // re-opening the transcript page shows the checkmark. Stale fingerprints
  // from prior regenerations are pruned at the same time. The room id
  // lives in root.activeRoomId — NOT `root.route.params.id` (we use a
  // single /meet route with state sub-views, not dynamic params).
  try {
    const fingerprint = await fingerprintSuggestion(item)
    const current = root.analysis?.applied_items || []
    const pruned = await pruneStaleApplied(current, root.analysis?.suggestions)
    const next = [...pruned, {
      bucket,
      fingerprint,
      target_id: String(targetId),
      applied_at: new Date().toISOString()
    }]
    // migrated 2026-04-27 to SDK route workspaceProject.meet.analysisAppliedItems
    const roomId = root.activeRoomId
    if (roomId) {
      try {
        await getSDK().execute('workspaceProject.meet.analysisAppliedItems', 'update',
          { roomId, payload: { applied_items: next } })
        root.update({ analysis: { ...root.analysis, applied_items: next } })
      } catch (err) {
        console.warn('[applySuggestion] applied_items write failed', err?.message || err)
      }
    }
  } catch (err) {
    console.warn('[applySuggestion] applied_items write skipped', err)
  }

  // Do NOT router-navigate here — `this.router()` doesn't exist on plain
  // function contexts and would throw. The caller (the click handler in
  // MeetSuggestionCard) should drive navigation via el.router after this
  // function returns.
  return targetId
}
